import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import {
  inheritingRolesFor,
  libraryErrorField,
  requiredLanguagesFor,
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
  type LibraryAssetType,
} from "@/api/library";
import {
  attributePartsFailure,
  isPartsContent,
  newDraftPart,
  toDraft,
  toPartsContent,
  type DraftPart,
  type PartProblem,
} from "@/lib/assetContent";
import { AUTHORED_SAFETY_SENTENCE } from "@/lib/adoptions";
import { ASSET_TYPE_LABELS, sectionForAssetType } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { LoadingState } from "@/ui/states";
import { FieldAnchorProvider } from "@/ui/validation/FieldAnchorContext";

import { PartsEditor } from "./PartsEditor";

/**
 * Create a shared library asset at v1, from scratch or as a copy (#33).
 *
 * WHY THIS EXISTS AT ALL. Seeded base canon is read-only by policy, and the
 * service says so in the refusal itself: "copy it into a new asset or override
 * per project". Without a create flow that instruction has no destination —
 * three of this ticket's four families ship with nothing but seeded rows, so
 * the editors would open onto content no clinician may change. The copy is what
 * turns a read-only library into an authorable one without ever forking base
 * canon in place.
 *
 * A COPY IS AN ORDINARY CREATE. There is no server-side copy route and none is
 * needed: the copy is this request carrying content read from the source's head
 * version. That keeps the new asset genuinely independent — `seeded: false`,
 * `lockedFloor: false`, its own version chain starting at v1 — rather than a
 * reference back to canon that would inherit canon's refusals. #366 makes that
 * literal for placement too: a copy captures its OWN pack coordinate, because
 * the source's is unique among the active assets of its type and inheriting it
 * would collide every time.
 *
 * The starting content is edited HERE rather than after creation because v1 is
 * a version like any other: it is immutable the moment it exists, and the
 * per-type part manifest is enforced on it. Creating first and fixing after
 * would leave a permanent, undeletable v1 that failed the manifest.
 */

/**
 * The types the parts editor covers.
 *
 * `questionnaire` belongs to #34 and `alert_matrix` to #35: both are typed
 * content with their own editors, so neither can be authored through a list of
 * pack-file documents. `background_set` is absent for a different reason: the
 * independent BG-set model pins the seeded, pack-owned sets and has no runtime
 * placement contract for a clinician-authored set. Offering Create or Copy
 * would therefore manufacture an asset that the project cannot serialize.
 *
 * `safety_track` JOINED THIS LIST IN #35. It is parts-shaped — the service's own
 * part manifest gives it the same screening prompt and steps a pathway has — and
 * the seeded guard floor is read-only base canon whose refusal says "copy it
 * into a new asset or override per project". Without a create destination that
 * instruction has nowhere to go, and locked-floor SEPARATION is precisely the
 * ability to author a clinician-owned track beside the floor rather than in
 * place of it. Copying does not weaken the floor: the copy is a new asset with
 * `seeded: false` and `lockedFloor: false`, and the floor itself stays
 * unarchivable and uneditable.
 *
 * `closing_conversation` JOINED IT IN #866 AND LEFT IT IN #867, which is the
 * handoff #866 wrote into this comment: "#867 owns the Lovable-shaped step
 * editor and the Project-side entry, exactly as #643 later replaced this dialog
 * for the Pathway family." `NewClosingConversationDialog` is that replacement.
 * The family is gone from this list rather than merely unreachable from its own
 * catalog, because this dialog's Type picker is reachable from every generic
 * section: leaving it here would offer a clinician standing in Rules a free-text
 * part key and a raw-JSON steps field, over a family whose steps document the
 * service refuses unless it carries at least one step and a completion
 * instruction. That is a form whose refusals a clinician cannot act on.
 *
 * `summary_prompt` JOINED IT IN #442, and for the same reason. Its manifest
 * entry is one required `prompt` document, which is the parts shape rather than
 * a shape needing an editor of its own — a form typing the INSIDE of a prompt
 * would assert a schema no authority in this repository publishes. Before this
 * it was the last of the nine types with no authoring surface at all: the only
 * summary prompt in existence was seeded base canon, so the surface opened onto
 * content nobody may change, and the refusal that says "copy it into a new
 * asset" had no destination. Adding it here supplies all three at once — the
 * section's create control, the route's editor, and that copy.
 */
export const CREATABLE_ASSET_TYPES = [
  "main_concern",
  "pathway",
  "pathway_variant",
  "language_rules",
  "safety_track",
  "summary_prompt",
] as const satisfies readonly LibraryAssetType[];

export type CreatableAssetType = (typeof CREATABLE_ASSET_TYPES)[number];

/**
 * The families #492 gave a canonical dialog of their own, which this one must
 * therefore not offer.
 *
 * `main_concern` is still parts-shaped and still creatable through
 * `named/NewMainConcernDialog`, which is where the canonical reference puts it.
 * What it may no longer reach is THIS dialog, because it authors content
 * through `PartsEditor`, whose free-text "Part key" input is exactly the
 * manual-key escape hatch #446's 2026-08-25 amendment forbids for this family.
 *
 * That matters even though the main-concern section does not open this dialog:
 * the Type control below reaches EVERY creatable type from whichever section
 * opened it, so without this the forbidden control stayed one selection away
 * from the "New asset" button in Pathways, Rules, Safety paths and Summary — a
 * hole no assertion on the named surface could catch, because the control is
 * not on it.
 *
 * It stays in `CREATABLE_ASSET_TYPES` because that list means "parts-shaped and
 * authorable", which remains true and is what `LibraryAssetRoute`'s dispatch
 * and `LibraryCatalog`'s create control read it for.
 */
const CANONICAL_OWN_DIALOG = [
  "main_concern",
  // #643 adds the Pathway family for the SAME reason and with the same
  // consequence. `NewPathwayDialog` creates a family default or a variant under
  // an explicit parent, from the service's own part manifest; this dialog would
  // reach the same two types through its Type control, with the forbidden part
  // key input attached. The hole this closes is the one the paragraph above
  // describes: the Type control reaches every creatable type from whichever
  // section opened it, so without this the key input stayed one selection away
  // from "New asset" in Rules, Safety paths and Summary.
  "pathway",
  "pathway_variant",
  // #863 adds the Summary prompt, and here the escape hatch is not only the
  // part key. This dialog's Pack placement field is a free-text coordinate, and
  // the committed MOD/NII equivalence contract forbids exactly that for this
  // family — "no free-form nested path" — while `POST /library/assets` refuses
  // a `placementCoordinate` carrying a '/' for a summary_prompt outright. So a
  // clinician reaching this dialog's Type control from Rules or Safety paths
  // could type `clinicial_summary`, a target the summary agent never opens, and
  // could not express the nested depression target at all.
  // `named/NewSummaryPromptDialog` selects the target from the server's own
  // catalog instead.
  "summary_prompt",
] as const satisfies readonly CreatableAssetType[];

/**
 * The types this generic dialog offers, in served order.
 *
 * A LATENT WIDENING SURVIVES THIS LIST, and it is recorded rather than fixed
 * here: `initialAssetType` is still a `CreatableAssetType`, so a caller could
 * open this dialog ON a canonical-dialog type and get that type's form beside
 * the part-key input its own Type control refuses to offer. No production
 * caller does — `LibraryCatalog` computes it only for sections it still serves,
 * and neither `chief-complaint` nor `pathways` is one — and narrowing the
 * parameter reaches a dozen `main_concern` arms in this dialog's own suite that
 * belong to #492. It is named in this pull request as a follow-up instead of
 * being rewritten from a Pathways ticket.
 */
export const GENERIC_DIALOG_TYPES = CREATABLE_ASSET_TYPES.filter(
  (assetType) =>
    !(CANONICAL_OWN_DIALOG as readonly string[]).includes(assetType),
);

export function isCreatableAssetType(
  assetType: LibraryAssetType,
): assetType is CreatableAssetType {
  return (CREATABLE_ASSET_TYPES as readonly string[]).includes(assetType);
}

/**
 * Where a new asset's files belong in a pack, per type (#366).
 *
 * Only the placeable types appear. `language_rules` and `background_set` map to
 * FIXED pack paths with no slug freedom, and `safety_track` targets a tree the
 * engine suppresses in every overlay — so the service refuses a coordinate on
 * all three, and offering a control for one would invite a refusal the
 * clinician cannot act on.
 *
 * `summary_prompt` is the fourth placeable type and gained its entry in #442,
 * with the same create flow it gained there. Its files land under the summary
 * agent's per-key tree rather than the conversation agent's, but the shape a
 * clinician types is the same one slug, so the sentence is the same sentence.
 *
 * The sentences say what a coordinate IS rather than restating the grammar the
 * service enforces — the service is the one authority on that, and a second
 * statement of a validation rule in the browser is what #300 spent a ticket
 * deleting. The immutability warning is here because it is the one thing the
 * refusal cannot say afterwards: there is no rename, so a typo costs an archive
 * and a recreate.
 */
const PLACEMENT_GUIDANCE: Partial<Record<CreatableAssetType, string>> = {
  main_concern:
    "The folder this main concern's files take in a pack, as one slug — for example 'panic'. It cannot be changed after the asset exists.",
  pathway:
    "The folder this pathway's files take in a pack, as one slug — for example 'anxiety'. It cannot be changed after the asset exists.",
  pathway_variant:
    "The parent pathway's own coordinate, then '/', then this variant's slug — for example 'anxiety/agoraphobia'. Leave it empty if the parent has no coordinate of its own, as base canon does: its placement comes from the pack manifest instead. It cannot be changed after the asset exists.",
  summary_prompt:
    "The folder this summary prompt's files take in a pack, as one slug — for example 'chief_complaint'. It cannot be changed after the asset exists.",
};

/**
 * The three types this dialog creates whose coordinate is required unconditionally.
 *
 * A `pathway_variant` is deliberately absent: whether it needs one is a fact
 * about its PARENT ROW, which this dialog cannot read, so the requirement is
 * left to the service and arrives as a refusal on the same control.
 *
 * `summary_prompt` is here because the service requires one for every placeable
 * type but that variant, and this list is what keeps the refusal off a control
 * the clinician has already filled in correctly: without it the button submits,
 * the service answers "a placement coordinate is required for this asset type",
 * and the message lands on the one field nothing asked them to complete.
 */
const PLACEMENT_REQUIRED: readonly CreatableAssetType[] = [
  "main_concern",
  "pathway",
  "summary_prompt",
];

const BLANK_SOURCE = "";
const NAME_MAX_LENGTH = 200;
const NOTE_MAX_LENGTH = 500;

interface CreateFieldErrors {
  name?: string;
  parentPathwayId?: string;
  placementCoordinate?: string;
  copiedFrom?: string;
}

/**
 * Which control a refused create belongs to.
 *
 * The service names it: a duplicate name comes back 409 with `field: "name"`,
 * an archived or unresolvable parent with `field: "parentPathwayId"`, a missing,
 * malformed or already-taken placement coordinate with
 * `field: "placementCoordinate"` (#366), an unusable copy source with
 * `field: "copiedFrom.assetId"` or `"copiedFrom.version"` (#954), and a manifest
 * breach 422 at `content.parts`. Reading the envelope's field rather than inferring from the
 * status is what lets the message sit on the control the clinician has to
 * change — and it is the only thing that can, since a coordinate collision and
 * a name collision are the same status on the same route.
 */
export function createFieldErrors(error: unknown): CreateFieldErrors {
  if (!(error instanceof ApiError)) {
    return {};
  }
  const field = libraryErrorField(error);
  if (field === "name") {
    return { name: error.message };
  }
  if (field === "parentPathwayId") {
    return { parentPathwayId: error.message };
  }
  if (field === "placementCoordinate") {
    return { placementCoordinate: error.message };
  }
  // #954: `copiedFrom.assetId` and `copiedFrom.version` both describe the
  // STARTING CONTENT the clinician chose, so both land on that one control —
  // the service names the half that is wrong, and the control is the same
  // either way. `startsWith`, not equality: the service addresses the member,
  // never the object.
  if (field?.startsWith("copiedFrom")) {
    return { copiedFrom: error.message };
  }
  if (error.status === 409) {
    // A conflict with no pointer is still a name collision on this route: every
    // other 409 the contract declares here names its own field.
    return { name: error.message };
  }
  return {};
}

export function NewAssetDialog({
  onClose,
  initialAssetType,
  copyFrom,
}: {
  onClose: () => void;
  initialAssetType?: CreatableAssetType;
  /** Opens with this asset preselected as the content source. */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const catalog = useLibraryAssetsQuery(false);
  // The SERVICE's placement policy, on the editor surface's own terms (#635).
  //
  // `POST /v1/library/assets` is where a clinician FIRST meets
  // `library.validate_required_languages` — "language_rules requires the parts
  // for 'he': base_rules." — so a create dialog without this read is the one
  // screen where the new refusal fires and nothing on it can act on the refusal:
  // no bilingual statement, and no "Add he documents" control, because
  // `missingRequiredLanguages` had an empty required list to compare against.
  // That is the flow the audit's own `Audit Blank Rules 20260828` came through.
  //
  // Projected on this dialog's OWN `assetType` state rather than on an asset,
  // because the type here is a live selection: the Type control below reaches
  // every generic creatable type, and the statement has to follow it.
  //
  // Same discipline as `AssetEditor`'s copy and as the part manifest before it:
  // it describes the SERVICE, so it never seeds `parts`, never blocks the form,
  // and an absent or still-loading document degrades to `[]` — no claim, no
  // control, and the server's refusal stays the authority.
  const registry = useLibraryPlacementRegistryQuery();
  const nameErrorId = useId();
  const parentErrorId = useId();
  const placementErrorId = useId();

  const [assetType, setAssetType] = useState<CreatableAssetType>(
    copyFrom && isCreatableAssetType(copyFrom.assetType)
      ? copyFrom.assetType
      : // #492: the default is the first type this dialog still OFFERS. It was
        // `main_concern`, which now has a canonical dialog of its own — leaving
        // it here would have opened this one on a type absent from its own Type
        // control, so the control and the form disagreed from the first frame.
        (initialAssetType ?? (GENERIC_DIALOG_TYPES[0] as CreatableAssetType)),
  );
  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [note, setNote] = useState(
    copyFrom
      ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`
      : "",
  );
  const [parentPathwayId, setParentPathwayId] = useState(
    copyFrom?.assetType === "pathway_variant" ? copyFrom.parentPathwayId : "",
  );
  // A copy captures its OWN coordinate (#366), and where it starts depends on
  // the SOURCE. An authored source's coordinate is unique among the active
  // assets of its type, so pre-filling it would offer a value guaranteed to be
  // refused — blank. Base canon's is not: a seeded row stores no coordinate
  // (its placement is the seed manifest's), the server derives and serves the
  // one it occupies, and a copy placed THERE is exactly the editable
  // replacement "copy it into a new asset" promises. So a copy of base canon
  // starts at the source's served placement; a clinician who wants a distinct
  // runtime key changes it here, and never has to guess an invisible one.
  const inheritedPlacement =
    copyFrom?.capabilities.seeded === true && copyFrom.placementCoordinate
      ? copyFrom.placementCoordinate
      : "";
  const [placementCoordinate, setPlacementCoordinate] =
    useState(inheritedPlacement);
  const [sourceId, setSourceId] = useState(copyFrom?.id ?? BLANK_SOURCE);
  const [parts, setParts] = useState<DraftPart[]>([
    newDraftPart("prompt_text"),
  ]);
  /** The source whose content `parts` currently reflects. */
  const [seededFrom, setSeededFrom] = useState(BLANK_SOURCE);
  const [clientProblems, setClientProblems] = useState<PartProblem[]>([]);

  const assets = catalog.data?.items ?? [];
  // The active catalog deliberately excludes archived assets. A direct copy,
  // however, already carries the exact source row the clinician chose, and an
  // archived asset remains readable/copyable even though it is no longer an
  // option in the active roster.
  const source =
    (copyFrom?.id === sourceId ? copyFrom : null) ??
    assets.find((asset) => asset.id === sourceId) ??
    null;
  const sourceContent = useAssetVersionQuery(
    source?.id ?? "",
    source ? source.latestVersion : null,
  );

  // Same render-time derivation as the editor: the starting parts ARE the
  // source's content, so an effect would flash a blank editor over content
  // already in hand. Guarded on the source id, so a clinician's subsequent
  // edits are never re-seeded away.
  if (
    source &&
    seededFrom !== source.id &&
    sourceContent.isSuccess &&
    sourceContent.data.assetId === source.id &&
    isPartsContent(sourceContent.data.content)
  ) {
    setParts(toDraft(sourceContent.data.content));
    setSeededFrom(source.id);
    setClientProblems([]);
  }

  // Non-archived assets of the chosen type. Copying across types is not
  // offered: a `main_concern` built from a `pathway`'s parts fails the
  // manifest, and the failure would arrive only after the clinician had named
  // and described the new asset. Every creatable type is parts-shaped, so a
  // candidate of the chosen type is always a usable source — the type system
  // enforces that half, since `CreatableAssetType` excludes the two typed
  // content shapes outright.
  const copyCandidates = [
    ...(copyFrom?.assetType === assetType ? [copyFrom] : []),
    ...assets.filter(
      (asset) => asset.assetType === assetType && asset.id !== copyFrom?.id,
    ),
  ];
  const pathways = assets.filter((asset) => asset.assetType === "pathway");

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const partsMessage =
    create.isError &&
    create.error instanceof ApiError &&
    libraryErrorField(create.error)?.startsWith("content")
      ? create.error.message
      : null;
  const generalMessage =
    create.isError && !partsMessage && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null;

  const serverProblems: PartProblem[] = partsMessage
    ? (() => {
        const indexes = attributePartsFailure(partsMessage, parts);
        return indexes.length > 0
          ? indexes.map((index) => ({ index, message: partsMessage }))
          : [{ index: null, message: partsMessage }];
      })()
    : [];
  const problems: PartProblem[] = [...clientProblems, ...serverProblems];

  const busy = create.isPending;
  const nameMissing = name.trim().length === 0;
  const parentMissing =
    assetType === "pathway_variant" && parentPathwayId === "";
  const familyGuidance = PLACEMENT_GUIDANCE[assetType];
  // The copy-of-base-canon case says what keeping the prefilled coordinate
  // MEANS — the consequence a clinician is choosing — beside the family's own
  // guidance, and only while the field still holds the source's placement.
  const placementGuidance =
    familyGuidance !== undefined &&
    inheritedPlacement !== "" &&
    placementCoordinate.trim() === inheritedPlacement
      ? `${familyGuidance} Prefilled with the base pack's own placement of "${copyFrom?.name ?? "the source"}": keeping it makes this copy replace the base pack's content at that runtime placement when a project adopts it. Change it to add a distinct placement instead.`
      : familyGuidance;
  // Only where the requirement is unconditional. A variant's depends on its
  // parent row, which this dialog cannot read, so gating the button on it here
  // would make a variant of base canon — whose parent is placed by the pack
  // manifest and carries no coordinate — uncreatable in the browser (#366).
  const placementMissing =
    PLACEMENT_REQUIRED.includes(assetType) &&
    placementCoordinate.trim().length === 0;
  // A copy whose source content has not arrived would be created from the blank
  // starting part instead — an asset silently missing everything it was meant
  // to copy. Fails closed until the content is in hand.
  const awaitingSource = source !== null && seededFrom !== source.id;

  function changeType(next: CreatableAssetType) {
    setAssetType(next);
    // The chosen source belongs to the old type and would now be a cross-type
    // copy, so it is dropped rather than silently kept.
    setSourceId(BLANK_SOURCE);
    setSeededFrom(BLANK_SOURCE);
    setParts([newDraftPart("prompt_text")]);
    setParentPathwayId("");
    // Dropped for the same reason as the parent: a coordinate's shape and its
    // uniqueness are both per type, so one typed for the old type describes a
    // placement the new type cannot have.
    setPlacementCoordinate("");
    setClientProblems([]);
    create.reset();
  }

  function submit() {
    const result = toPartsContent(parts);
    if (!result.ok) {
      setClientProblems(result.problems);
      return;
    }
    setClientProblems([]);
    // Sent only where the type can carry one, and only when the clinician typed
    // something: an empty string is not a coordinate, and the three unplaceable
    // types are refused outright for carrying the property at all.
    //
    // NO `copiedFrom`, DELIBERATELY (#954). That field proves an engine-owned
    // structured document is a copy, and only `pathway` / `pathway_variant`
    // have such roles — both of which are in `CANONICAL_OWN_DIALOG` above and
    // create through `named/NewPathwayDialog`, which does send it. Nothing this
    // dialog can build carries a document the service would check, so a source
    // reference from here would be provenance nothing verifies — and this
    // dialog, unlike that one, lets a clinician EDIT the seeded parts before
    // submitting, so the claim would frequently be false. If a family this
    // dialog serves ever gains a protected role, the field is already on the
    // request and the seeded-source state (`seededFrom`, `source.latestVersion`)
    // is already here.
    const placement = placementCoordinate.trim();
    create.mutate(
      {
        assetType,
        name: name.trim(),
        content: result.content,
        note: note.trim() === "" ? null : note.trim(),
        ...(assetType === "pathway_variant" ? { parentPathwayId } : {}),
        ...(placementGuidance !== undefined && placement !== ""
          ? { placementCoordinate: placement }
          : {}),
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(
            `/library/${sectionForAssetType(created.assetType)}/${created.id}`,
          );
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="w-[min(56rem,calc(100vw-2rem))]"
        title={copyFrom ? `Copy "${copyFrom.name}"` : "New library asset"}
        description="Creates a shared asset at v1, owned by clinicians rather than by base canon. It is editable from the moment it exists, and every project must adopt it explicitly before it changes anything."
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                nameMissing ||
                parentMissing ||
                placementMissing ||
                awaitingSource
              }
              onClick={submit}
            >
              Create asset
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          <Field label="Type">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={assetType}
                disabled={copyFrom !== undefined}
                onChange={(event) =>
                  changeType(event.target.value as CreatableAssetType)
                }
              >
                {GENERIC_DIALOG_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ASSET_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {assetType === "safety_track" ? (
            // Creation stays enabled — authoring beside the floor is supported
            // (#35) and the asset is a real versioned library row. What no
            // runtime supports yet is adopting one, and saying so AFTER the
            // clinician has authored ten parts is the dishonesty the parity
            // audit recorded (#636). Same sentence the adoption surface
            // disables with, so the two tell one story.
            <Notice tone="warning">{AUTHORED_SAFETY_SENTENCE}</Notice>
          ) : null}

          {assetType === "pathway_variant" ? (
            <Field
              label="Parent pathway"
              description="A variant's identity is its explicit parent, never its name."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Select
                    id={inputId}
                    value={parentPathwayId}
                    aria-invalid={
                      fieldErrors.parentPathwayId !== undefined || undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.parentPathwayId ? parentErrorId : undefined,
                    )}
                    onChange={(event) => setParentPathwayId(event.target.value)}
                  >
                    <option value="">Choose a pathway…</option>
                    {pathways.map((pathway) => (
                      <option key={pathway.id} value={pathway.id}>
                        {pathway.name}
                      </option>
                    ))}
                  </Select>
                  {fieldErrors.parentPathwayId ? (
                    <FieldError
                      id={parentErrorId}
                      message={fieldErrors.parentPathwayId}
                    />
                  ) : null}
                </>
              )}
            </Field>
          ) : null}

          {placementGuidance !== undefined ? (
            <Field label="Pack placement" description={placementGuidance}>
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={placementCoordinate}
                    aria-invalid={
                      fieldErrors.placementCoordinate !== undefined || undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.placementCoordinate
                        ? placementErrorId
                        : undefined,
                    )}
                    onChange={(event) =>
                      setPlacementCoordinate(event.target.value)
                    }
                  />
                  {fieldErrors.placementCoordinate ? (
                    <FieldError
                      id={placementErrorId}
                      message={fieldErrors.placementCoordinate}
                    />
                  ) : null}
                </>
              )}
            </Field>
          ) : null}

          <Field
            label="Starting content"
            description="Copying reads the source's current version. The new asset is independent from then on — it carries none of the source's seeded or locked state."
          >
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                value={sourceId}
                disabled={copyFrom !== undefined}
                aria-describedby={describedBy}
                onChange={(event) => {
                  const next = event.target.value;
                  setSourceId(next);
                  if (next === BLANK_SOURCE) {
                    setSeededFrom(BLANK_SOURCE);
                    setParts([newDraftPart("prompt_text")]);
                  }
                  setClientProblems([]);
                  create.reset();
                }}
              >
                <option value={BLANK_SOURCE}>Start from an empty part</option>
                {copyCandidates.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    Copy of {asset.name} ({asset.latestVersion})
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {awaitingSource ? (
            <LoadingState
              label={`Loading content from ${source?.name ?? "the source asset"}`}
              rows={2}
            />
          ) : (
            <FieldAnchorProvider>
              <PartsEditor
                parts={parts}
                onChange={(next) => {
                  setParts(next);
                  setClientProblems([]);
                  create.reset();
                }}
                readOnly={false}
                readOnlyReason={null}
                problems={problems}
                requiredLanguages={requiredLanguagesFor(
                  registry.data,
                  assetType,
                )}
                inheritingRoles={inheritingRolesFor(registry.data, assetType)}
              />
            </FieldAnchorProvider>
          )}

          <Field
            label="Version note"
            description="Recorded against v1 and shown in the version history."
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                value={note}
                maxLength={NOTE_MAX_LENGTH}
                aria-describedby={describedBy}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>

          {generalMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {generalMessage}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
